import { EntityNameEnum, EntitySubtypeKind, SiblingRelationType } from "@ppl/graphql-space-api";


export interface EntityRelationRule {
  entityName: EntityNameEnum;
  entitySubtypes?: EntitySubtypeKind[];
  forRoot?: boolean;
  relations: {
    entityName: EntityNameEnum;
    siblingRelationTypes: SiblingRelationType[];
  }[];
}

export function getEntityRelationEntities(entityName: EntityNameEnum | null): EntityNameEnum[] {
  if (entityName === null) {
    return EntityRelationRules.filter(rule => rule.forRoot).map(rule => rule.entityName);
  }

  let ruleEntityName = entityName;

  if ([EntityNameEnum.Lead, EntityNameEnum.Opportunity].includes(entityName)) {
    ruleEntityName = EntityNameEnum.LeadOppty;
  }

  if ([EntityNameEnum.Task, EntityNameEnum.Appointment].includes(entityName)) {
    ruleEntityName = EntityNameEnum.Activity;
  }

  const rule = getEntityRelationRule(ruleEntityName);

  return rule.relations.map(relation => relation.entityName);
}

export function getEntityRelationRule(entityName: EntityNameEnum): EntityRelationRule | null {
  return EntityRelationRules.find(rule => rule.entityName === entityName) || null;
}

export const EntityRelationRules: EntityRelationRule[] = [
  {
    entityName: EntityNameEnum.Account,
    forRoot: true,
    relations: [
      {
        entityName: EntityNameEnum.Activity,
        siblingRelationTypes: [SiblingRelationType.LastFinishedActivity, SiblingRelationType.NextScheduledActivity, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Contact,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.LeadOppty,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Message,
        siblingRelationTypes: [SiblingRelationType.LastMessage, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Note,
        siblingRelationTypes: [SiblingRelationType.LastNote, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.OnlineForm,
        siblingRelationTypes: [SiblingRelationType.LastOnlineFormResponse, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Product,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Project,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Quote,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      }
    ]
  },
  {
    entityName: EntityNameEnum.Activity,
    entitySubtypes: [EntitySubtypeKind.Task, EntitySubtypeKind.Appointment],
    forRoot: true,
    relations: [
      {
        entityName: EntityNameEnum.Account,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.ActivityComment,
        siblingRelationTypes: [SiblingRelationType.LastActivityComment, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Contact,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.CustomEntity,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.LeadOppty,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Project,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Quote,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      }
    ]
  },
  {
    entityName: EntityNameEnum.Contact,
    forRoot: true,
    relations: [
      {
        entityName: EntityNameEnum.Account,
        siblingRelationTypes: [SiblingRelationType.Primary, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Activity,
        siblingRelationTypes: [SiblingRelationType.LastFinishedActivity, SiblingRelationType.NextScheduledActivity, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.LeadOppty,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Message,
        siblingRelationTypes: [SiblingRelationType.LastMessage, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Note,
        siblingRelationTypes: [SiblingRelationType.LastNote, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.OnlineForm,
        siblingRelationTypes: [SiblingRelationType.LastOnlineFormResponse, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Product,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Project,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Quote,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      }
    ]
  },
  {
    entityName: EntityNameEnum.CustomEntity,
    forRoot: true,
    relations: [
      {
        entityName: EntityNameEnum.Activity,
        siblingRelationTypes: [SiblingRelationType.LastFinishedActivity, SiblingRelationType.NextScheduledActivity, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Note,
        siblingRelationTypes: [SiblingRelationType.LastNote, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.OnlineForm,
        siblingRelationTypes: [SiblingRelationType.LastOnlineFormResponse, SiblingRelationType.All]
      }
    ]
  },
  {
    entityName: EntityNameEnum.Email,
    forRoot: true,
    relations: [
      {
        entityName: EntityNameEnum.Account,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Contact,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.CustomEntity,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.LeadOppty,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Project,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Quote,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      }
    ]
  },
  {
    entityName: EntityNameEnum.LeadOppty,
    entitySubtypes: [EntitySubtypeKind.Lead, EntitySubtypeKind.Opportunity],
    forRoot: true,
    relations: [
      {
        entityName: EntityNameEnum.Account,
        siblingRelationTypes: [SiblingRelationType.Primary, SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Activity,
        siblingRelationTypes: [SiblingRelationType.LastFinishedActivity, SiblingRelationType.NextScheduledActivity, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Contact,
        siblingRelationTypes: [SiblingRelationType.Primary, SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Message,
        siblingRelationTypes: [SiblingRelationType.LastMessage, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Note,
        siblingRelationTypes: [SiblingRelationType.LastNote, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.OnlineForm,
        siblingRelationTypes: [SiblingRelationType.LastOnlineFormResponse, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Product,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Project,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Quote,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.RevenueRecognition,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Timeframe,
        siblingRelationTypes: []
      }
    ]
  },
  {
    entityName: EntityNameEnum.Message,
    entitySubtypes: [EntitySubtypeKind.Memo, EntitySubtypeKind.Email],
    forRoot: false,
    relations: [
      {
        entityName: EntityNameEnum.Account,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Contact,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.CustomEntity,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.LeadOppty,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Project,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Quote,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      }
    ]
  },
  {
    entityName: EntityNameEnum.Note,
    forRoot: false,
    relations: [
      {
        entityName: EntityNameEnum.Account,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Contact,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.CustomEntity,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.LeadOppty,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Project,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Quote,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      }
    ]
  },
  {
    entityName: EntityNameEnum.OnlineForm,
    forRoot: false,
    relations: []
  },
  {
    entityName: EntityNameEnum.Product,
    forRoot: true,
    relations: [
      {
        entityName: EntityNameEnum.Account,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Contact,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.LeadOppty,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Quote,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      }
    ]
  },
  {
    entityName: EntityNameEnum.Project,
    forRoot: true,
    relations: [
      {
        entityName: EntityNameEnum.Account,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Activity,
        siblingRelationTypes: [SiblingRelationType.LastFinishedActivity, SiblingRelationType.NextScheduledActivity, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Contact,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.LeadOppty,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Message,
        siblingRelationTypes: [SiblingRelationType.LastMessage, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Note,
        siblingRelationTypes: [SiblingRelationType.LastNote, SiblingRelationType.All]
      }
    ]
  },
  {
    entityName: EntityNameEnum.Quote,
    forRoot: true,
    relations: [
      {
        entityName: EntityNameEnum.Account,
        siblingRelationTypes: [SiblingRelationType.Primary, SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Activity,
        siblingRelationTypes: [SiblingRelationType.LastFinishedActivity, SiblingRelationType.NextScheduledActivity, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Contact,
        siblingRelationTypes: [SiblingRelationType.Primary, SiblingRelationType.Aggregation, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.LeadOppty,
        siblingRelationTypes: [SiblingRelationType.Primary]
      },
      {
        entityName: EntityNameEnum.Message,
        siblingRelationTypes: [SiblingRelationType.LastMessage, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Note,
        siblingRelationTypes: [SiblingRelationType.LastNote, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.OnlineForm,
        siblingRelationTypes: [SiblingRelationType.LastOnlineFormResponse, SiblingRelationType.All]
      },
      {
        entityName: EntityNameEnum.Product,
        siblingRelationTypes: [SiblingRelationType.Aggregation, SiblingRelationType.All]
      }
    ]
  },
  {
    entityName: EntityNameEnum.RevenueRecognition,
    forRoot: false,
    relations: []
  },
  {
    entityName: EntityNameEnum.Timeframe,
    forRoot: false,
    relations: []
  }
];
